import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Auth, signInWithCustomToken, signInWithEmailAndPassword } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FormPasswordComponent } from '../../shared/components/form-password/form-password.component';
import { FormButtonComponent } from '../../shared/components/form-button/form-button.component';
import { AuthService } from '../../core/services/auth.service';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { AuthThemeDirective } from 'src/app/core/directives/auth-theme-directive';
import { BRAND_ASSETS } from 'src/app/core/constants/brand-assets';

/**
 * Flow types for invitation acceptance
 * - login_required: User has Firebase account, just needs to log in
 * - password_setup: User profile exists, needs to set password
 * - account_creation: New user, needs to create Firebase account
 */
type InvitationFlowType = 'login_required' | 'password_setup' | 'account_creation';

interface InvitationValidationResponse {
  invitation: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: number;
    expiresAt: string;
  };
  organization: {
    id: string;
    name: string;
  } | null;
  profile: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  } | null;
  flow: {
    type: InvitationFlowType;
    hasFirebaseAccount: boolean;
    description: string;
  };
  source: 'organization' | 'profile';
}

/**
 * Accept Invitation Page Component
 *
 * Allows users to accept invitations sent by admins or organization owners.
 * Uses the unified GET /auth/invitations/:token endpoint to validate tokens
 * from both invitation tables (organization and profile) in a single call.
 *
 * Handles three different flows based on user's account status:
 * 1. login_required: User already has Firebase account - prompts to log in
 * 2. password_setup: User profile exists without Firebase - creates Firebase account
 * 3. account_creation: New user - creates profile and Firebase account
 */
@Component({
  selector: 'app-accept-invitation',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    FormPasswordComponent,
    FormButtonComponent,
    AuthThemeDirective,
    MaxLengthDirective,
  ],
  templateUrl: './accept-invitation.component.html',
  styleUrls: ['./accept-invitation.component.scss']
})
export class AcceptInvitationComponent implements OnInit {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;

  // Forms for different flows
  passwordForm: FormGroup;
  loginForm: FormGroup;

  // State
  loading: boolean = false;
  loadingInvitation: boolean = true;
  errorMessage: string = '';
  successMessage: string = '';
  invitationToken: string = '';
  invitationDetails: InvitationValidationResponse | null = null;
  invitationError: string = '';
  flowType: InvitationFlowType | null = null;

  private auth = inject(Auth);
  private authService = inject(AuthService);

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Form for password setup / account creation flows
    this.passwordForm = this.fb.group({
      firstName: ['', [Validators.maxLength(100)]],
      lastName: ['', [Validators.maxLength(100)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });

    // Form for login_required flow
    this.loginForm = this.fb.group({
      password: ['', [Validators.required]]
    });
  }

  async ngOnInit(): Promise<void> {
    // Get invitation token from query params
    this.invitationToken = this.route.snapshot.queryParams['token'];

    if (!this.invitationToken) {
      this.invitationError = 'Invalid invitation link. No token provided.';
      this.loadingInvitation = false;
      return;
    }

    // Validate invitation and determine flow
    await this.validateInvitation();
  }

  /**
   * Validate invitation token using the unified endpoint.
   * A single call to GET /auth/invitations/:token searches both invitation tables
   * and returns the source type, flow type, and all needed fields.
   */
  async validateInvitation(): Promise<void> {
    this.loadingInvitation = true;
    this.invitationError = '';

    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/auth/invitations/${this.invitationToken}`)
      );

      if (response.success) {
        this.invitationDetails = response.data;
        this.flowType = response.data.flow.type;

        // Pre-fill names from the invitation, falling back to an existing profile
        const { invitation, profile } = response.data;
        this.passwordForm.patchValue({
          firstName: invitation?.firstName || profile?.firstName || '',
          lastName: invitation?.lastName || profile?.lastName || '',
        });
      } else {
        this.invitationError = response.error || 'Failed to validate invitation';
      }
    } catch (error: any) {
      console.error('Validate invitation error:', error);
      this.handleInvitationError(error);
    } finally {
      this.loadingInvitation = false;
    }
  }

  /**
   * Handle invitation validation errors with friendly messages
   */
  private handleInvitationError(error: any): void {
    if (error.status === 404) {
      this.invitationError = 'This invitation link is invalid or has already been used. Please contact American Drone Network for assistance.';
    } else if (error.status === 410) {
      // Handle expired, revoked, or already accepted
      const errorCode = error.error?.code;
      if (errorCode === 'INVITATION_EXPIRED') {
        this.invitationError = 'This invitation has expired. Please contact American Drone Network for a new invitation.';
      } else if (errorCode === 'INVITATION_REVOKED') {
        this.invitationError = 'This invitation has been cancelled. Please contact American Drone Network for assistance.';
      } else if (errorCode === 'INVITATION_ACCEPTED') {
        this.invitationError = 'This invitation has already been accepted. You can sign in with your account.';
      } else {
        this.invitationError = error.error?.error || 'This invitation is no longer valid. Please contact American Drone Network for assistance.';
      }
    } else {
      this.invitationError = 'Unable to validate invitation. Please try again or contact American Drone Network for assistance.';
    }
  }

  /**
   * Custom validator to check if passwords match
   */
  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }

    return null;
  }

  /**
   * Handle login form submission (for login_required flow)
   */
  async onLoginSubmit(): Promise<void> {
    if (this.loginForm.invalid || !this.invitationDetails) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const { password } = this.loginForm.value;
      const email = this.invitationDetails.profile?.email || this.invitationDetails.invitation.email;

      // Sign in with Firebase
      await signInWithEmailAndPassword(this.auth, email, password);

      // Accept the invitation via the unified endpoint
      const response = await firstValueFrom(
        this.http.post<any>(this.acceptUrl, { flowType: 'login_required' })
      );

      if (response.success) {
        this.successMessage = 'Invitation accepted! Redirecting to dashboard...';

        // Refresh session to pick up updated custom claims and load organization context.
        // Non-fatal: invitation was already accepted, so redirect even if refresh fails.
        try {
          await this.authService.refreshSession();
        } catch (refreshError) {
          console.error('Session refresh error:', refreshError);
        }

        // Redirect to dashboard
        setTimeout(() => {
          this.router.navigate(['/dashboard']);
        }, 1500);
      } else {
        this.errorMessage = response.error || 'Failed to accept invitation';
      }
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        this.errorMessage = 'Invalid password. Please try again.';
      } else if (error.code === 'auth/user-not-found') {
        this.errorMessage = 'Account not found. Please contact support.';
      } else if (error.code === 'auth/too-many-requests') {
        this.errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.status === 410) {
        this.errorMessage = error.error?.error || 'This invitation is no longer valid.';
      } else {
        this.errorMessage = 'Failed to sign in. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * Handle password setup form submission (for password_setup and account_creation flows)
   */
  async onPasswordSubmit(): Promise<void> {
    if (this.passwordForm.invalid || !this.invitationDetails) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const { password, firstName, lastName } = this.passwordForm.value;

      // Accept invitation and create/link Firebase account via unified endpoint
      const response = await firstValueFrom(
        this.http.post<any>(this.acceptUrl, {
          password,
          firstName: firstName?.trim() || null,
          lastName: lastName?.trim() || null,
          flowType: this.flowType,
        })
      );

      if (response.success) {
        this.successMessage = 'Account created successfully! Signing you in...';

        // Sign in with custom token
        if (response.data.customToken) {
          await signInWithCustomToken(this.auth, response.data.customToken);

          // Refresh session to pick up updated custom claims and load organization context.
          // Non-fatal: sign-in succeeded, so redirect even if refresh fails.
          try {
            await this.authService.refreshSession();
          } catch (refreshError) {
            console.error('Session refresh after invitation acceptance failed:', refreshError);
          }

          // Redirect to dashboard after short delay
          setTimeout(() => {
            this.router.navigate(['/dashboard']);
          }, 1500);
        } else {
          // Fallback: redirect to login page
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 2000);
        }
      } else {
        this.errorMessage = response.error || 'Failed to accept invitation';
      }
    } catch (error: any) {
      console.error('Accept invitation error:', error);
      if (error.status === 400) {
        this.errorMessage = error.error?.error || 'Invalid password. Must be at least 8 characters.';
      } else if (error.status === 404) {
        this.errorMessage = 'Invitation not found.';
      } else if (error.status === 409) {
        this.errorMessage = 'An account already exists with this email. Please sign in instead.';
      } else if (error.status === 410) {
        this.errorMessage = error.error?.error || 'This invitation is no longer valid.';
      } else {
        this.errorMessage = 'Failed to accept invitation. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * Build the accept URL using the unified endpoint.
   * Uses POST /auth/invitations/:token/accept for both invitation types.
   */
  private get acceptUrl(): string {
    return `${environment.apiUrl}/auth/invitations/${this.invitationToken}/accept`;
  }

  /**
   * Get password mismatch error state
   */
  get passwordMismatch(): boolean {
    return this.passwordForm.hasError('passwordMismatch') &&
           this.passwordForm.get('confirmPassword')?.touched || false;
  }

  /**
   * Get role display name from bitwise role value
   */
  getRoleDisplayName(role: number): string {
    const roles: string[] = [];
    if (role & 4) roles.push('Owner');
    if (role & 8) roles.push('Chief Supervisor');
    if (role & 2) roles.push('Pilot in Command');
    if (role & 1) roles.push('Pilot');
    return roles.join(', ') || 'Member';
  }

  /**
   * Navigate to login page
   */
  goToLogin(): void {
    this.router.navigate(['/login']);
  }
}
